// Generic detail view for any workspace request (memo, IT asset, desk, HR
// service, service hub, technical query, material requisition, ...):
// fields, attachments, approval routing, history & comments, and the
// actions available to the current user (approve / return / reject,
// cancel, acknowledge, comment).
import React, { useState } from 'react';
import { wsAPI } from '../services/api';
import {
  Modal, Badge, ReqStatus, RequestRouting, RequestDecision, FileChip, Note, Loading, Textarea, Spin,
  useAsync, useAction, promptText, money, fmtDate, fmtDateTime, titleCase,
} from './ui';

export const TYPE_LABELS = {
  memo: 'Approval memo', project_letter: 'Project letter', project_code: 'Project code request', it_asset: 'IT asset request',
  desk: 'Desk & office space', service: 'Service request', hr_service: 'HR & admin service', material_requisition: 'Material requisition',
  technical_query: 'Technical query', policy_query: 'Policy query', profile_correction: 'Profile correction', role_change: 'Role change', cost_mapping: 'Cost mapping',
};

const HIDDEN_KEYS = ['items', 'quotes', 'body', 'acknowledged', 'acknowledgedAt'];
const labelOf = (k) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
const fmtVal = (v) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(v))) return fmtDate(v);
  return String(v);
};

export function RequestFields({ request }) {
  const r = request;
  const base = [
    ['Reference', r.refNo], ['Type', `${TYPE_LABELS[r.type] || r.type}${r.subType ? ` · ${r.subType}` : ''}`], ['Requester', `${r.requesterName}${r.requesterCode ? ` (${r.requesterCode})` : ''}`],
    ['Project', r.project], ['Cost center', r.costCenter], ['Cost category', r.costCategory],
    ['Amount', r.amount ? money(r.amount) : null], ['Required by', r.requiredBy ? fmtDate(r.requiredBy) : null], ['Priority', r.priority !== 'Normal' ? r.priority : null],
    ['Location', r.location || r.site], ['Submitted', r.submittedAt ? fmtDateTime(r.submittedAt) : null],
  ].filter(([, v]) => v);
  const extra = Object.entries(r.data || {}).filter(([k, v]) => !HIDDEN_KEYS.includes(k) && v !== '' && v !== null && v !== undefined && typeof v !== 'object');
  return (
    <div className="tse-form-grid c3">
      {[...base, ...extra.map(([k, v]) => [labelOf(k), fmtVal(v)])].map(([k, v]) => (
        <div key={k}><div className="tse-muted tse-small">{k}</div><div className="tse-strong" style={{ wordBreak: 'break-word' }}>{v}</div></div>
      ))}
    </div>
  );
}

export function ItemsTable({ items }) {
  if (!items || !items.length) return null;
  return (
    <div className="tse-card" style={{ boxShadow: 'none' }}>
      <div className="tse-table-wrap">
        <table className="tse-table compact">
          <thead><tr><th>Item</th><th>Requested</th><th>Stock</th><th>To procure</th></tr></thead>
          <tbody>{items.map((i, k) => <tr key={k}><td>{i.item}</td><td>{i.requested} {i.unit || 'nos'}</td><td>{i.stock} {i.unit || 'nos'}</td><td>{i.toProcure} {i.unit || 'nos'}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function RequestDetail({ id, onClose, onChanged, stageFields, extra }) {
  const { data, loading, reload, setData } = useAsync(() => (id ? wsAPI.getRequest(id).then((d) => d.request) : Promise.resolve(null)), [id]);
  const [comment, setComment] = useState('');
  const [busy, run] = useAction();
  const r = data;
  const changed = (req) => { if (req) setData({ ...req, canAct: req.canAct }); reload(); onChanged && onChanged(); };

  const cancel = async () => {
    const remarks = await promptText({ title: 'Cancel this request?', label: 'Reason', required: false, confirmText: 'Cancel request', color: '#c43c31' });
    if (remarks === null) return;
    const x = await run('cancel', () => wsAPI.cancelRequest(r._id, remarks), 'Request cancelled');
    if (x) changed();
  };
  const ack = async () => {
    const x = await run('ack', () => wsAPI.acknowledgeRequest(r._id), 'Acknowledged — request closed');
    if (x) changed();
  };
  const submit = async () => {
    const x = await run('submit', () => wsAPI.submitRequest(r._id), 'Submitted for approval');
    if (x) changed();
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    const x = await run('comment', () => wsAPI.commentRequest(r._id, comment.trim()), false);
    if (x) { setComment(''); reload(); }
  };

  return (
    <Modal open={!!id} onClose={onClose} size="xl" title={r ? r.title : 'Request'} sub={r ? `${r.refNo || 'Draft'} · ${TYPE_LABELS[r.type] || r.type}` : ''}
      footer={r && (
        <>
          {r.isOwner && ['DRAFT', 'RETURNED'].includes(r.status) && <button type="button" className="tse-btn" onClick={submit} disabled={!!busy}>{busy === 'submit' ? <Spin /> : null}Submit for approval</button>}
          {r.isOwner && ['DRAFT', 'PENDING', 'RETURNED'].includes(r.status) && <button type="button" className="tse-btn danger-outline" onClick={cancel} disabled={!!busy}>Cancel request</button>}
          {r.isOwner && r.status === 'APPROVED' && ['it_asset', 'desk', 'hr_service', 'service'].includes(r.type) && <button type="button" className="tse-btn success" onClick={ack} disabled={!!busy}>Acknowledge receipt</button>}
          <button type="button" className="tse-btn gray" onClick={onClose}>Close</button>
        </>
      )}>
      {loading || !r ? <Loading /> : (
        <div className="tse-split" style={{ gridTemplateColumns: 'minmax(0, 1fr) 330px' }}>
          <div className="tse-stack" style={{ gap: 8 }}>
            <div className="tse-row"><ReqStatus request={r} />{r.pendingWith && <span className="tse-small tse-muted">with {r.pendingWith}</span>}</div>
            {r.status === 'RETURNED' && <Note tone="warn" title="Returned for clarification">{(r.history || []).filter((h) => h.action === 'RETURNED').slice(-1)[0]?.remarks}</Note>}
            <RequestFields request={r} />
            {r.description && <div><div className="tse-muted tse-small">Details</div><div style={{ whiteSpace: 'pre-wrap' }}>{r.description}</div></div>}
            {r.data?.justification && !r.description && <div><div className="tse-muted tse-small">Justification</div><div style={{ whiteSpace: 'pre-wrap' }}>{r.data.justification}</div></div>}
            <ItemsTable items={r.data?.items} />
            {extra && extra(r, changed)}
            {(r.attachments || []).length > 0 && <div className="tse-grid g-2">{r.attachments.map((a, i) => <FileChip key={a.s3Key || i} file={a} />)}</div>}
            <div>
              <div className="tse-label" style={{ marginBottom: 8 }}>Activity</div>
              <div className="tse-stack" style={{ gap: 8 }}>
                {(r.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="tse-row" style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'nowrap', fontSize: '0.791rem' }}>
                    <Badge tone={{ APPROVED: 'approved', REJECTED: 'rejected', RETURNED: 'pending', COMMENT: 'blue', SUBMITTED: 'info', CANCELLED: 'gray', ACKNOWLEDGED: 'approved' }[h.action] || 'gray'}>{titleCase(h.action)}</Badge>
                    <div style={{ flex: 1 }}>{h.stage ? <strong>{h.stage}: </strong> : null}{h.remarks || ''}<div className="tse-muted tse-small">{h.byName || ''} · {fmtDateTime(h.at)}</div></div>
                  </div>
                ))}
              </div>
              <div className="tse-row" style={{ marginTop: 10, flexWrap: 'nowrap' }}>
                <Textarea rows={1} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment for the requester / approvers" style={{ minHeight: 42 }} />
                <button type="button" className="tse-btn outline" onClick={addComment} disabled={busy === 'comment' || !comment.trim()}>Comment</button>
              </div>
            </div>
          </div>
          <div className="tse-stack">
            <div className="tse-card" style={{ boxShadow: 'none' }}><div className="tse-card-head" style={{ fontSize: '0.86rem' }}>Approval routing</div><div className="tse-card-body" style={{ paddingTop: 0 }}><RequestRouting request={r} /></div></div>
            {r.canAct && (
              <div className="tse-card" style={{ boxShadow: 'none' }}>
                <div className="tse-card-head" style={{ fontSize: '0.86rem' }}>Approver actions</div>
                <div className="tse-card-body" style={{ paddingTop: 0 }}>
                  <RequestDecision request={r} onDone={changed} stageFields={stageFields ? (sd, set) => stageFields(r, sd, set) : undefined} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
